import { buildApiUrl } from '../utils/apiSetup';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const parseError = async (response, fallback) => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.detail) && data.detail.length > 0) {
        return data.detail[0].msg || fallback;
      }
    }
  } catch {
    /* ignore parse failure */
  }
  return fallback;
};

export async function fetchPayrollRuns(signal) {
  const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load payroll runs'));
  }
  return response.json();
}

export async function fetchDashboardKpis(payrollRunId, signal) {
  const params = payrollRunId ? `?payroll_run_id=${payrollRunId}` : '';
  const response = await fetch(buildApiUrl(`/api/v1/reports/dashboard/kpis${params}`), {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load dashboard KPIs'));
  }
  return response.json();
}

export async function fetchReportPreview(payrollRunId, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/reports/preview/${payrollRunId}`), {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load synopsis preview'));
  }
  return response.json();
}

export async function fetchSalaryStatementPreview(payrollRunId, secCode, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/reports/salary-statement/${payrollRunId}/${secCode}?format=json`),
    { headers: authHeaders(), signal }
  );
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load salary statement preview'));
  }
  return response.json();
}

export async function searchEmployees(query, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/benfish-employees/search?query=${encodeURIComponent(query)}&limit=20`),
    { headers: authHeaders(), signal }
  );
  if (!response.ok) return [];
  const data = await response.json();
  return data.employees || data || [];
}

export async function fetchEmployeeLoans(employeeId, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/reports/employee-loans/${employeeId}`), {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) return { loans: [] };
  return response.json();
}

export async function fetchHrReport(reportType, params = {}, signal) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  });
  const qs = searchParams.toString();
  const response = await fetch(buildApiUrl(`/v1/reports/hr-report/${reportType}${qs ? `?${qs}` : ''}`),
    { headers: authHeaders(), signal }
  );
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load report data'));
  }
  return response.json();
}

export async function downloadAuthenticatedFile(url, filename) {
  const fullUrl = buildApiUrl(url);
  const response = await fetch(fullUrl, { headers: authHeaders() });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to download file'));
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('Received invalid HTML response from server instead of requested file.');
  }
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

export async function fetchRetiredEmployeesReport(fromMonth, toMonth, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/reports/retired-employees?from_month=${fromMonth}&to_month=${toMonth}`),
    { headers: authHeaders(), signal }
  );
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load retired employees report'));
  }
  return response.json();
}

export async function fetchPastRetiredEmployeesReport(year, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/reports/past-retired-employees?year=${year}`),
    { headers: authHeaders(), signal }
  );
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load past retired employees report'));
  }
  return response.json();
}

export async function fetchServiceDurationReport(filters, signal) {
  const params = new URLSearchParams(filters);
  const response = await fetch(buildApiUrl(`/api/v1/reports/service-duration?${params}`), {
    headers: authHeaders(),
    signal
  });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load service duration report'));
  }
  return response.json();
}

export const HR_REPORT_CONFIG = [
  {
    id: 'attendance-register',
    title: 'Attendance Register',
    description: 'Daily attendance status, check-in/out, late marks, and total hours.',
    color: 'bg-green-600',
    columns: [
      { key: 'employee_code', label: 'Employee Code' },
      { key: 'name', label: 'Name' },
      { key: 'department', label: 'Department' },
      { key: 'date', label: 'Date' },
      { key: 'shift', label: 'Shift' },
      { key: 'check_in', label: 'In' },
      { key: 'check_out', label: 'Out' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    id: 'leave-register',
    title: 'Leave Register',
    description: 'Approved, pending, rejected, and escalated leave governance summary.',
    color: 'bg-blue-600',
    columns: [
      { key: 'application_number', label: 'Application No.' },
      { key: 'employee_name', label: 'Employee' },
      { key: 'department', label: 'Department' },
      { key: 'leave_type', label: 'Leave Type' },
      { key: 'from_date', label: 'From' },
      { key: 'to_date', label: 'To' },
      { key: 'days', label: 'Days' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    id: 'employee-master',
    title: 'Employee Master Summary',
    description: 'Employee demographic, department, designation, and service profile summary.',
    color: 'bg-indigo-600',
    columns: [
      { key: 'employee_code', label: 'Employee Code' },
      { key: 'name', label: 'Name' },
      { key: 'department', label: 'Department' },
      { key: 'designation', label: 'Designation' },
      { key: 'employee_type', label: 'Type' },
      { key: 'joining_date', label: 'Joining Date' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    id: 'loan-advance',
    title: 'Loan & Advance Summary',
    description: 'Employee loan balances, EMI recovery, advance balances, and closure status.',
    color: 'bg-orange-600',
    columns: [
      { key: 'employee', label: 'Employee' },
      { key: 'type', label: 'Loan Type' },
      { key: 'principal', label: 'Principal' },
      { key: 'outstanding', label: 'Outstanding' },
      { key: 'emi', label: 'EMI' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    id: 'compliance-summary',
    title: 'HR Compliance Summary',
    description: 'Exception report for missing attendance, overtime, leaves, and shift conflicts.',
    color: 'bg-red-600',
    columns: [
      { key: 'category', label: 'Category' },
      { key: 'department', label: 'Department' },
      { key: 'count', label: 'Count' },
      { key: 'severity', label: 'Severity' },
      { key: 'action_required', label: 'Action Required' },
    ],
  },
];

export async function fetchFinalSettlementReport(year, department, signal) {
  const params = new URLSearchParams({ year });
  if (department && department !== 'ALL') {
    params.set('department', department);
  }
  const response = await fetch(buildApiUrl(`/api/v1/reports/final-settlement-report?${params}`), {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load Final Settlement report'));
  }
  return response.json();
}
